import styles from "./Contract.module.css";
import contractDummy from "../assets/contract-dummy.jpg";

import { useNavigate } from "react-router-dom";
import apiService from "../../services/apiService";
import { useUser } from "../../contexts/UserContext";
import { useEffect, useState } from "react";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export default function Contract() {
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();
  const [checkingNavigate, setCheckingNavigate] = useState(true);
  const [validationStates, setValidationStates] = useState(null);

  useEffect(() => {
    const fetchDealerDetails = async () => {
      try {
        const response = await apiService.dealerOnboardingDetails({
          action: "get",
        });
        if (!response.data) {
          console.error("No data received from dealerOnboardingDetails API");
          return;
        }
        if (response.data.error) {
          console.error(
            "Error fetching dealer details:",
            response.data.message
          );
          return;
        }
        const validation_states = response.data.stage_validation_states;
        setValidationStates(validation_states);
        // Navigation logic (unchanged)
        const navigateToPendingStage = async (validation_states) => {
          await new Promise((r) => setTimeout(r, 500));
          if (validation_states.details.status === "pending") {
            return navigate("/dealer-onboarding/dealer-details");
          } else if (validation_states.payment.status === "pending") {
            return navigate("/dealer-onboarding/subscription");
          } else if (validation_states.documents.status === "pending") {
            return navigate("/dealer-onboarding/upload-docs");
          } else {
            setCheckingNavigate(false);
            return;
          }
        };
        await navigateToPendingStage(validation_states);
      } catch (error) {
        console.error("Error fetching dealer details:", error);
      }
    };
    fetchDealerDetails();
  }, []);
  // Helper: get validation_pending stages
  const getPendingValidations = (states) => {
    if (!states) return [];
    return Object.entries(states)
      .filter(
        ([key, value]) =>
          value.status === "validation_pending" || value.status === "not_valid"
      )
      .map(([key]) => key);
  };

  // Helper: check if all except contract are success
  const canShowContractButtons = (states) => {
    if (!states) return false;
    const requiredStages = ["details", "payment", "documents"];
    return (
      requiredStages.every((stage) => states[stage]?.status === "validated") &&
      (validationStates.contract.status === "created" ||
        validationStates.contract.status === "downloaded")
    );
  };

  const handleDownloadContract = async () => {
    try {
      const response = await apiService.post("downloadDealerContract");
      console.log("Download contract response:", response);
      if (response.data.error) {
        alert("Error downloading contract: " + response.data.message);
        return;
      }

      window.open(response.data.contract.url, "_blank");
    } catch (error) {
      alert("Error downloading contract: " + error.message);
    }
  };

  const handleUploadSignedContract = async (file) => {
    try {
      const fileBase64 = await fileToBase64(file);
      const fileBase64Data = fileBase64.split(",")[1];

      const payload = {
        file: fileBase64Data,
        metadata: {
          contentType: file.type,
          originalName: file.name,
          size: file.size,
        },
      };

      const response = await apiService.post("uploadSignedDealerContract", payload);
      if (response.data.error) {
        alert("Error uploading signed contract: " + response.data.message);
        return;
      }

      alert("Signed contract uploaded successfully!");
    } catch (error) {
      alert("Error uploading signed contract");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <h1 className={styles.title}>Download our Contract</h1>
        <h2>Instructions before uploading</h2>
        <ol>
          <li>
            Finalize the Contract: Ensure all terms, clauses, and obligations
            are accurately documented and that necessary attachments or exhibits
            are included.
          </li>
          <li>
            Identify Parties: Clearly state the full legal names and addresses
            of all parties involved to avoid ambiguity.
          </li>
          <li>
            Review and Revise: Thoroughly examine the document for accuracy,
            completeness, clarity, and ensure it reflects the agreed-upon terms
            and conditions, possibly involving legal counsel for complex
            agreements.
          </li>
          <li>
            Negotiation (if applicable): Clarify any ambiguous terms, address
            concerns, and make necessary adjustments based on feedback to ensure
            mutual understanding and agreement.
          </li>
          <li>
            Incorporate Signature Fields: Include designated spaces for
            signatures of all parties involved, according to top.legal.
          </li>
          <li>
            Obtain Approvals: Secure the required internal approvals from legal,
            finance, and other relevant departments before sending the contract
            for signature.
          </li>
        </ol>
        <div className={styles.buttonGroup}>
          {canShowContractButtons(validationStates) ? (
            <>
              <button
                className={styles.downloadButton}
                disabled={
                  validationStates.contract.status !== "created" &&
                  validationStates.contract.status !== "downloaded"
                }
                onClick={handleDownloadContract}
              >
                Download Contract
              </button>
              <button
                className={styles.uploadButton}
                disabled={validationStates.contract.status !== "downloaded"}
                onClick={() => {
                  const fileInput = document.createElement("input");
                  fileInput.type = "file";
                  fileInput.accept = ".pdf,.doc,.docx";
                  fileInput.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleUploadSignedContract(file);
                    }
                  };
                  fileInput.click();
                }}
              >
                Upload Signed Contract
              </button>
            </>
          ) : validationStates &&
            validationStates.contract.status !== "uploaded" &&
            getPendingValidations(validationStates).length === 0 ? (
            <div className={styles.pendingValidationMessage}>
              The contract will be available for download once it has been
              uploaded by our backoffice team.
            </div>
          ) : (
            validationStates &&
            getPendingValidations(validationStates).length > 0 && (
              <div className={styles.pendingValidationMessage}>
                <h2 className={styles.pendingValidationTitle}>
                  Pending Validation
                </h2>
                <ul className={styles.pendingValidationList}>
                  {getPendingValidations(validationStates).map((stage) => (
                    <li key={stage} className={styles.pendingValidationItem}>
                      {stage.charAt(0).toUpperCase() + stage.slice(1)}{" "}
                      {validationStates[stage].status === "not_valid"
                        ? " - invalid info, please review the comments."
                        : "validation is pending"}
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>
      </div>
      <div className={styles.right}>
        <img src={contractDummy} alt="Example Contract Image" />
      </div>
    </div>
  );
}
